/**
 * AWAC agent-text localization shims.
 * ---------------------------------------------------------------------------
 * The conversation language is injected at creation time (createConversation
 * `language` field -> MessagingSession.EndUserLanguage -> agent YAML rule), so
 * the LLM replies in the site language. Two strings, however, are STATIC
 * deployment text sent before the LLM ever runs and are defined once (in
 * English) in the agent YAML `messages:` block:
 *
 *   - the welcome message
 *   - the generic error message
 *
 * Those cannot be localized per-conversation in the YAML, so the client
 * translates them at render time via EXACT string match — safe because the
 * source strings are deployment constants we own. Anything else passes
 * through untouched.
 */

import { getLang } from "./i18n";

/**
 * Exact EN deployment strings -> FR. Keys must match the YAML / Apex verbatim
 * (straight apostrophes and em-dashes exactly as emitted by the source).
 */
const FR_BY_EXACT_EN = {
    // --- Agent YAML static messages ---
    "Hi, my name is Lia. I can answer your questions about the homes, pricing, and the Le Lib Mont-Saint-Hilaire project — and whenever you're ready, just let me know and I can set up a visit with our sales team. How may I help?":
        "Bonjour, je m'appelle Lia. Je peux répondre à vos questions sur les résidences, les prix et le projet Le Lib Mont-Saint-Hilaire — et dès que vous êtes prêt, dites-le-moi et j'organiserai une visite avec notre équipe des ventes. Comment puis-je vous aider?",
    "Oops, something went wrong. Try again!":
        "Oups, une erreur s'est produite. Veuillez réessayer.",

    // --- ShowUnits (Apex envelope intro texts) ---
    "Here are the matching units — swipe through and tell me which ones you'd like to explore.":
        "Voici les unités correspondantes — faites-les défiler et dites-moi lesquelles vous aimeriez explorer.",
    // Legacy wording kept so messages sent by the previous Apex version (or
    // replayed conversation history) still localize.
    "Here are some available units — swipe through and tell me which ones you'd like to explore.":
        "Voici des unités disponibles — faites-les défiler et dites-moi lesquelles vous aimeriez explorer.",
    "I couldn't find an exact match for that, so here are the closest available options — swipe through and tell me which ones you'd like to explore.":
        "Je n'ai pas trouvé de correspondance exacte, alors voici les options disponibles les plus proches — faites-les défiler et dites-moi lesquelles vous aimeriez explorer.",
    "I couldn't find available units matching that just now — let me know what you're looking for and I'll pull options.":
        "Je n'ai pas trouvé d'unités disponibles correspondant à votre demande pour le moment — dites-moi ce que vous cherchez et je vous proposerai des options.",

    // --- ShowNeighborhood ---
    "Here's what's around the project — tap a pin to see details, or ask me about anything nearby.":
        "Voici ce qui entoure le projet — touchez une épingle pour voir les détails, ou posez-moi vos questions sur les environs.",
    "I couldn't pull the neighbourhood highlights just now — ask me about schools, healthcare or transit and I'll fill you in.":
        "Je n'ai pas pu récupérer les points d'intérêt du quartier pour le moment — demandez-moi pour les écoles, les soins de santé ou le transport et je vous renseignerai.",

    // --- ShowFloorPlans ---
    "Here are our current floor plans — take a look and let me know if any catch your eye.":
        "Voici nos plans d'étage actuels — jetez-y un coup d'œil et dites-moi si l'un d'eux vous plaît.",

    // --- GetVisitSlots / BookVisit (EN variants; Apex already emits FR when
    // the conversation locale is fr, this covers EN-locale sessions viewed in FR) ---
    "Here are the available times for a visit of Lib Mont Saint-Hilaire — pick the slot that works for you:":
        "Voici les disponibilités pour une visite de Lib Mont Saint-Hilaire — choisissez la plage qui vous convient :",
    "Sorry — that slot was just taken. Here are the updated available times:":
        "Désolé, cette plage vient tout juste d'être réservée. Voici les disponibilités à jour :",
    "Something went wrong while booking. Here are the current available times:":
        "Un imprévu est survenu lors de la réservation. Voici les disponibilités à jour :"
};

/**
 * Pattern-based FR translations for dynamic agent strings (booking confirm
 * carries unit name + date + time). Each entry: [regex, replacement].
 */
const FR_BY_PATTERN = [
    [
        /^You['\u2019]re all set! Your visit of (.+) is booked for (.+) at (.+)\.$/,
        "C'est confirmé ! Votre visite de $1 est réservée le $2 à $3."
    ]
];

/**
 * Exact EN quick-reply / option labels -> FR. These are display-only: the
 * string sent back to the agent stays the original EN `send` value so the
 * Apex actions keep matching.
 */
const FR_OPTION_LABELS = {
    "Book a visit": "Réserver une visite",
    "Show me the neighborhood": "Montre-moi le quartier",
    "See floor plans": "Voir les plans",
    "What's the deposit?": "Quel est le dépôt?",
    "Tell me about the 4½": "Parle-moi du 4½",
    "None of these work": "Aucune ne me convient"
};

/**
 * Localize a static agent/system string for the active site language.
 * @param {string} text - the rendered agent message text.
 * @param {boolean} isEndUserMessage - end-user bubbles are never touched.
 * @returns {string}
 */
export function localizeAgentText(text, isEndUserMessage) {
    if (isEndUserMessage || typeof text !== "string") {
        return text;
    }
    if (getLang() !== "fr") {
        return text;
    }
    const trimmed = text.trim();
    const translated = FR_BY_EXACT_EN[trimmed];
    if (translated) {
        return translated;
    }
    for (let i = 0; i < FR_BY_PATTERN.length; i++) {
        const [pattern, replacement] = FR_BY_PATTERN[i];
        if (pattern.test(trimmed)) {
            return trimmed.replace(pattern, replacement);
        }
    }
    return text;
}

/**
 * Localize a quick-reply / option button label for display. The value sent
 * back to the agent is NOT affected — callers must keep sending the original
 * EN string so server-side intent matching keeps working.
 * @param {string} label
 * @returns {string}
 */
export function localizeOptionLabel(label) {
    if (typeof label !== "string" || getLang() !== "fr") {
        return label;
    }
    return FR_OPTION_LABELS[label.trim()] || label;
}
